import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sqScalingTableDefinitionStore, sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { useHasValidFeature } from '@/licenseManagement/licenseManagement.store';
import { setAutoFocusTableDefinitionTab } from '@/main/layout.actions';
import { isScalingTableEditorEnabled } from '@/services/systemConfiguration.utilities';
import { resetTableDefinition, setIsApplyingModel } from '@/tableDefinitionEditor/tableDefinition.actions';
import { EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { resetTableEditorStore } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { setActiveTool, setItem } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { CreateApplyInScalingTableModal } from '@/tools/models/CreateApplyInScalingTableModal.molecule';
import { LockModelConfirmation } from '@/tools/models/LockModelConfirmation.molecule';
import { resetModel } from '@/tools/models/model.actions';
import { fetchModelForEditing } from '@/tools/models/model.api';
import { MODEL_TYPES } from '@/tools/models/model.types';
import { duplicateModel, fetchModel, renameToUniqueDerivedFeatureNames } from '@/tools/models/model.utilities';
import { useModelLibraryActions } from '@/tools/models/modelLibrary.store';
import { useCompatibilityCheck } from '@/tools/models/useCompatibilityCheck.hook';
import { useLockingEnabled } from '@/tools/models/useLockingEnabled.hook';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { fetchModels } from '@/workbook/workbook.actions';
import { setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { sqModelsApi } from '@seeqdev/api/endpoint/ModelsApi';
import { Icon, TriggerWithDropdown } from '@seeqdev/qomponents';
import type { SelectedSearchItem } from '@/search/search.types';
import type { ModelVersionMetadata } from '@/tools/models/model.types';

export interface ModelRowItem extends SelectedSearchItem {
  isAssetGroupItem: boolean;
  includedProperties: Record<string, any>;
  parameters: any[];
  id: string;
  name: string;
  type: 'Model' | 'ModelContainer';
  ancestors: any[];
  hasChildren: boolean;
  hasActiveChildren: boolean;
  isArchived: boolean;
  isRedacted: boolean;
  /** Version and draft metadata, present when Locking is enabled */
  versionMetadata?: ModelVersionMetadata;
}

interface ModelItemMenuProps {
  item: ModelRowItem;
  /** Whether the user is viewing the draft version of this model. */
  isViewingDraft?: boolean;
}

export const ModelItemMenu = ({ item, isViewingDraft = false }: ModelItemMenuProps) => {
  const { t } = useTranslation();
  const editorType = EDITOR_TYPES.SCALING;
  const hasScalingTables = useHasValidFeature(Feature.Scaling_Tables);

  const workbookId = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.stateParams.workbookId);
  const scalingTableColumns = useFluxPath(sqScalingTableDefinitionStore, () => sqScalingTableDefinitionStore.columns);

  const [isItemSearchModalOpen, setIsItemSearchModalOpen] = useState(false);
  const [selectedScalingTable, setSelectedScalingTable] = useState<SelectedSearchItem | undefined>(undefined);
  const [showLockConfirmation, setShowLockConfirmation] = useState(false);

  const cancelScalingTableSelection = () => {
    setIsItemSearchModalOpen(false);
    setSelectedScalingTable(undefined);
  };

  const lockingEnabled = useLockingEnabled();
  const { clearDraftToggle } = useModelLibraryActions();
  const hasDraft = item.versionMetadata?.hasDraft ?? false;
  const draftVersionId = item.versionMetadata?.draftVersionId;

  const { findings, featureNameMap } = useCompatibilityCheck(
    lockingEnabled && hasDraft ? item.id : undefined,
    lockingEnabled && hasDraft ? draftVersionId : undefined,
  );

  // When Locking is enabled, the edit action label changes based on draft state
  const editLabel = lockingEnabled
    ? hasDraft
      ? t('MODELS.EDIT_DRAFT')
      : t('MODELS.CREATE_NEW_DRAFT')
    : t('MODELS.EDIT_MODEL_TOOL_PANE');
  const editIcon = lockingEnabled && !hasDraft ? 'fa-plus' : 'fa-pencil';

  return (
    <>
      <TriggerWithDropdown
        triggerIcon={
          <Icon
            type="theme"
            icon="fc-more"
            extraClassNames="tw:p-1 tw:cursor-pointer tw:rounded-sm"
            testId="modelItemMenuButton"
          />
        }
        onContainerClick={(e) => e.stopPropagation()}
        dropdownItems={[
          {
            icon: editIcon,
            iconType: 'theme',
            testId: 'modelEdit',
            label: editLabel,
            onClick: async (e) => {
              e?.stopPropagation();
              try {
                resetModel(MODEL_TYPES.TOOL);
                await fetchModelForEditing(item.id, MODEL_TYPES.TOOL);
                setActiveTool(TREND_TOOLS.MODEL_EDIT);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
              }
            },
          },
          {
            icon: 'fa-info-circle',
            iconType: 'theme',
            testId: 'modelProperties',
            label: t('PROPERTIES.HEADER'),
            onClick: async (e) => {
              e?.stopPropagation();
              try {
                if (isViewingDraft) {
                  await fetchModelForEditing(item.id, MODEL_TYPES.TOOL);
                } else {
                  await fetchModel(item.id, MODEL_TYPES.TOOL);
                }
                setActiveTool(TREND_TOOLS.PROPERTIES);
                setItem(item);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
              }
            },
          },
          {
            icon: 'fc-copy',
            iconType: 'theme',
            testId: 'modelDuplicate',
            label: t('MODELS.DUPLICATE_MODEL'),
            onClick: async (e) => {
              e?.stopPropagation();
              try {
                const newModel = await duplicateModel(item, workbookId);
                successToast({
                  messageKey: 'MODELS.DUPLICATE_SUCCESS',
                  messageParams: { originalName: item.name, duplicatedName: newModel.name },
                });
                // Refetch models to get the new model in the list, but don't await because we open edit pane next
                fetchModels([workbookId]);
                resetModel(MODEL_TYPES.TOOL);
                await fetchModelForEditing(newModel.id, MODEL_TYPES.TOOL);
                setActiveTool(TREND_TOOLS.MODEL_EDIT);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
              }
            },
          },
          {
            icon: 'fa-chart-area',
            iconType: 'theme',
            testId: 'applyModelOnce',
            label: t('MODELS.APPLY_MODEL_ONCE'),
            onClick: async (e) => {
              e?.stopPropagation();
              try {
                resetModel(MODEL_TYPES.TOOL);
                await fetchModel(item.id, MODEL_TYPES.TOOL);
                setActiveTool(TREND_TOOLS.MODEL_APPLY);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
              }
            },
          },
          // Lock action: only for Enterprise users with Locking, when model has a draft (unlocked version to lock)
          ...(lockingEnabled && hasDraft
            ? [
                {
                  icon: 'fa-lock',
                  iconType: 'theme' as const,
                  testId: 'modelLock',
                  label: t('MODELS.LOCK_MODEL'),
                  onClick: (e: any) => {
                    e?.stopPropagation();
                    setShowLockConfirmation(true);
                  },
                },
              ]
            : []),
          ...(isScalingTableEditorEnabled() && hasScalingTables
            ? [
                {
                  icon: 'fc fc-table',
                  iconType: 'theme',
                  testId: 'applyToScalingTable',
                  label: t('MODELS.APPLY_TO_SCALING_TABLE'),
                  onClick: (e: any) => {
                    e?.stopPropagation();
                    setIsItemSearchModalOpen(true);
                  },
                },
              ]
            : []),
        ]}
      />
      {isItemSearchModalOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <CreateApplyInScalingTableModal
            header="MODELS.MODAL_HEADER"
            helpText="MODELS.MODAL_HELP_TEXT"
            onClose={cancelScalingTableSelection}
            onSubmit={() => {
              setIsItemSearchModalOpen(false);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              if (selectedScalingTable) {
                editTableDefinition(selectedScalingTable, editorType).then(async () => {
                  await fetchModel(item.id, MODEL_TYPES.SCALING);
                  renameToUniqueDerivedFeatureNames(scalingTableColumns, editorType);
                  setIsApplyingModel(EDITOR_TYPES.SCALING, true);
                });
              }
              setSelectedScalingTable(undefined);
            }}
            onCreateNewItem={async () => {
              setIsItemSearchModalOpen(false);
              resetItemFinder();
              resetTableDefinition(editorType);
              resetTableEditorStore(editorType);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              await fetchModel(item.id, MODEL_TYPES.SCALING);
              setIsApplyingModel(EDITOR_TYPES.SCALING, true);
            }}
            onSelectItem={(item) => setSelectedScalingTable(item)}
            selectedItem={selectedScalingTable}
            iconPartialTooltipKey="MODELS"
          />
        </div>
      )}
      {showLockConfirmation && (
        <div onClick={(e) => e.stopPropagation()}>
          <LockModelConfirmation
            onClose={() => setShowLockConfirmation(false)}
            findings={findings}
            featureNamesByCanonicalId={featureNameMap}
            onConfirm={async () => {
              try {
                await sqModelsApi.publishModelVersion({ versionId: draftVersionId! }, { modelId: item.id });
                clearDraftToggle(item.id);
                successToast({ messageKey: 'MODELS.LOCK_SUCCESS_MESSAGE' });
                fetchModels([workbookId]);
              } catch (e) {
                errorToast({ httpResponseOrError: e });
                throw e;
              }
            }}
          />
        </div>
      )}
    </>
  );
};
